import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, Shield, Clock, User, MessageCircle, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { AdminVerificationReset } from "@/components/AdminVerificationReset";

interface BlockedAttempt {
  id: string;
  job_id: string;
  sender_id: string;
  attempted_content: string;
  blocked_reason: string;
  detected_info: any;
  created_at: string;
  job: {
    title: string;
    customer: {
      full_name: string;
      email: string;
    };
    tradesman: {
      full_name: string;
      email: string;
    };
  };
  sender: {
    full_name: string;
    email: string;
    user_type: string;
  };
}

export const AdminSecurityDashboard = () => {
  const [blockedAttempts, setBlockedAttempts] = useState<BlockedAttempt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { isAdmin, isLoading: authLoading } = useAdminAuth();

  useEffect(() => {
    fetchBlockedAttempts();
    const cleanup = setupRealtimeSubscription();
    return cleanup;
  }, []);


  const fetchBlockedAttempts = async () => {
    try {
      const { data, error } = await supabase
        .from("blocked_attempts")
        .select(`
          *,
          job:jobs!blocked_attempts_job_id_fkey(
            title,
            customer:profiles!jobs_customer_id_fkey(full_name, email),
            tradesman:profiles!jobs_tradesman_id_fkey(full_name, email)
          ),
          sender:profiles!blocked_attempts_sender_id_fkey(full_name, email, user_type)
        `)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      setBlockedAttempts(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading blocked attempts",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel(`blocked-attempts-channel-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'blocked_attempts'
        },
        (payload) => {
          console.log('New blocked attempt:', payload);
          fetchBlockedAttempts();
          toast({
            title: "🚨 New Security Alert",
            description: "A user attempted to share contact information",
            variant: "destructive",
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const getViolationColor = (reason: string) => {
    if (reason.includes('phone')) return 'bg-red-100 text-red-800 border-red-300';
    if (reason.includes('email')) return 'bg-orange-100 text-orange-800 border-orange-300';
    if (reason.includes('external')) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getSeverityIcon = (reason: string) => {
    if (reason.includes('phone') || reason.includes('email')) {
      return <AlertTriangle className="h-4 w-4 text-red-600" />;
    }
    return <Shield className="h-4 w-4 text-orange-600" />;
  };

  if (authLoading || isLoading) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="text-center mt-4 text-muted-foreground">Loading security dashboard...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-destructive" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You don't have permission to access the Security Dashboard. Admin privileges are required.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Security Dashboard</h1>
          <p className="text-muted-foreground">Monitor security violations and manage user verification</p>
        </div>
      </div>

      <Tabs defaultValue="monitoring" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="monitoring" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Security Monitoring
          </TabsTrigger>
          <TabsTrigger value="verification" className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4" />
            Verification Reset
          </TabsTrigger>
        </TabsList>

        <TabsContent value="monitoring" className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Total Blocked</p>
                    <p className="text-2xl font-bold">{blockedAttempts.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Today</p>
                    <p className="text-2xl font-bold">
                      {blockedAttempts.filter(attempt => 
                        new Date(attempt.created_at).toDateString() === new Date().toDateString()
                      ).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Most Common</p>
                    <p className="text-lg font-bold">Phone Numbers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Blocked Attempts List */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Blocked Attempts</CardTitle>
              <CardDescription>
                Real-time monitoring of contact information sharing attempts
              </CardDescription>
            </CardHeader>
            <CardContent>
              {blockedAttempts.length === 0 ? (
                <div className="text-center py-12">
                  <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-green-800 mb-2">All Clear!</h3>
                  <p className="text-green-700">No security violations detected</p>
                </div>
              ) : (
                <ScrollArea className="h-[600px]">
                  <div className="space-y-4">
                    {blockedAttempts.map((attempt) => (
                      <Card key={attempt.id} className="border-l-4 border-l-red-500">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {getSeverityIcon(attempt.blocked_reason)}
                              <div>
                                <h4 className="font-semibold">{attempt.job?.title || "Unknown Job"}</h4>
                                <p className="text-sm text-muted-foreground">
                                  {format(new Date(attempt.created_at), "PPP 'at' p")}
                                </p>
                              </div>
                            </div>
                            <Badge 
                              variant="outline"
                              className={getViolationColor(attempt.blocked_reason)}
                            >
                              {attempt.blocked_reason.replace('_', ' ').toUpperCase()}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <h5 className="font-medium mb-2 flex items-center gap-1">
                                <User className="h-4 w-4" />
                                Violator Details
                              </h5>
                              <div className="space-y-1 text-sm">
                                <p><strong>Name:</strong> {attempt.sender?.full_name}</p>
                                <p><strong>Email:</strong> {attempt.sender?.email}</p>
                                <p><strong>Type:</strong> {attempt.sender?.user_type}</p>
                              </div>
                            </div>

                            <div>
                              <h5 className="font-medium mb-2">Job Participants</h5>
                              <div className="space-y-1 text-sm">
                                <p><strong>Customer:</strong> {attempt.job?.customer?.full_name}</p>
                                <p><strong>Tradesman:</strong> {attempt.job?.tradesman?.full_name}</p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
                            <h5 className="font-medium mb-2 text-red-800">Attempted Message:</h5>
                            <p className="text-sm text-red-700 font-mono bg-background p-2 rounded border">
                              "{attempt.attempted_content}"
                            </p>
                          </div>

                          {attempt.detected_info && Object.keys(attempt.detected_info).length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded p-3">
                              <h5 className="font-medium mb-2 text-amber-800">Detected Information:</h5>
                              <div className="text-sm text-amber-700 space-y-1">
                                {attempt.detected_info.phone_matches && (
                                  <p><strong>Phone Numbers:</strong> {attempt.detected_info.phone_matches.join(', ')}</p>
                                )}
                                {attempt.detected_info.email_matches && (
                                  <p><strong>Email Addresses:</strong> {attempt.detected_info.email_matches.join(', ')}</p>
                                )}
                                {attempt.detected_info.external_matches && (
                                  <p><strong>External References:</strong> {attempt.detected_info.external_matches.join(', ')}</p>
                                )}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="verification">
          <AdminVerificationReset />
        </TabsContent>
      </Tabs>
    </div>
  );
};